"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiWave } from "@/generated/models/ApiWave";
import { DropSize } from "@/helpers/waves/drop.helpers";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Drop, { DropLocation } from "../drops/Drop";
const DESKTOP_SIDE_PADDING = 12;
const MOBILE_BREAKPOINT = 1024;
const DESKTOP_MAX_WIDTH = 672;
interface WaveDescriptionPopoverProps {
  readonly wave: ApiWave;
  readonly children: React.ReactNode;
  readonly ariaLabel: string;
  readonly triggerClassName?: string | undefined;
  readonly align?: "left" | "center" | "right" | undefined;
}
interface PanelPlacement {
  readonly top?: number;
  readonly bottom?: number;
  readonly left?: number;
  readonly right?: number;
  readonly width?: number;
  readonly maxHeight?: number;
}
const getDesktopLeft = (align: "left" | "center" | "right", buttonRect: DOMRect, panelWidth: number): number => {
  if (align === "right") {
    return buttonRect.right - panelWidth;
  }
  if (align === "center") {
    return buttonRect.left + (buttonRect.width - panelWidth) / 2;
  }
  return buttonRect.left;
};
const getPlacement = (buttonRect: DOMRect, panelHeight: number, align: "left" | "center" | "right"): PanelPlacement => {
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  const topSpace = buttonRect.top;
  const bottomSpace = window.innerHeight - buttonRect.bottom;
  const placeBelow = bottomSpace >= panelHeight || bottomSpace >= topSpace;
  const maxHeight = Math.max(120, (placeBelow ? bottomSpace : topSpace) - DESKTOP_SIDE_PADDING);
  const verticalPlacement = placeBelow ? {
    top: buttonRect.bottom + 8
  } : {
    bottom: window.innerHeight - buttonRect.top + 8
  };
  if (isMobile) {
    return {
      ...verticalPlacement,
      left: DESKTOP_SIDE_PADDING,
      right: DESKTOP_SIDE_PADDING,
      maxHeight
    };
  }
  const panelWidth = Math.min(DESKTOP_MAX_WIDTH, window.innerWidth - DESKTOP_SIDE_PADDING * 2);
  const preferredLeft = getDesktopLeft(align, buttonRect, panelWidth);
  const clampedLeft = Math.min(Math.max(preferredLeft, DESKTOP_SIDE_PADDING), window.innerWidth - panelWidth - DESKTOP_SIDE_PADDING);
  return {
    ...verticalPlacement,
    left: clampedLeft,
    width: panelWidth,
    maxHeight
  };
};
const WaveDescriptionPopover: React.FC<WaveDescriptionPopoverProps> = t0 => {
  const $ = _c(21);
  const {
    wave,
    children,
    ariaLabel,
    triggerClassName,
    align: t1
  } = t0;
  const align = t1 === undefined ? "left" : t1;
  const [isOpen, setIsOpen] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [placement, setPlacement] = useState(t2);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  let t3;
  let t4;
  if ($[1] !== align || $[2] !== isOpen) {
    t3 = () => {
      if (!isOpen) {
        return;
      }
      const updatePlacement = () => {
        const buttonRect = triggerRef.current?.getBoundingClientRect();
        if (!buttonRect) {
          return;
        }
        const panelHeight = panelRef.current?.getBoundingClientRect().height ?? 320;
        setPlacement(getPlacement(buttonRect, panelHeight, align));
      };
      updatePlacement();
      const rafId = window.requestAnimationFrame(updatePlacement);
      window.addEventListener("resize", updatePlacement);
      return () => {
        window.cancelAnimationFrame(rafId);
        window.removeEventListener("resize", updatePlacement);
      };
    };
    t4 = [align, isOpen];
    $[1] = align;
    $[2] = isOpen;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[5] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        return;
      }
      const onKeyDown = event => {
        if (event.key === "Escape") {
          setIsOpen(false);
        }
      };
      const onPointerDown = event_0 => {
        const target = event_0.target as Node | null;
        if (!target) {
          return;
        }
        if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) {
          return;
        }
        setIsOpen(false);
      };
      document.addEventListener("keydown", onKeyDown);
      document.addEventListener("mousedown", onPointerDown);
      document.addEventListener("touchstart", onPointerDown);
      return () => {
        document.removeEventListener("keydown", onKeyDown);
        document.removeEventListener("mousedown", onPointerDown);
        document.removeEventListener("touchstart", onPointerDown);
      };
    };
    t6 = [isOpen];
    $[5] = isOpen;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  if (!wave.description_drop.id) {
    return null;
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setIsOpen(_temp);
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== ariaLabel || $[10] !== children || $[11] !== isOpen || $[12] !== triggerClassName) {
    t8 = <button ref={triggerRef} type="button" onClick={t7} aria-label={ariaLabel} aria-haspopup="dialog" aria-expanded={isOpen} className={triggerClassName}>{children}</button>;
    $[9] = ariaLabel;
    $[10] = children;
    $[11] = isOpen;
    $[12] = triggerClassName;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== isOpen || $[15] !== placement || $[16] !== wave.description_drop) {
    t9 = isOpen && createPortal(<div ref={panelRef} style={{
      position: "absolute",
      top: placement.top,
      bottom: placement.bottom,
      left: placement.left,
      right: placement.right,
      width: placement.width,
      maxHeight: placement.maxHeight
    }} className="tw-z-[1200] tw-space-y-1 tw-overflow-y-auto tw-overflow-x-hidden tw-rounded-lg tw-bg-iron-800 tw-p-1 tw-shadow-xl tw-ring-1 tw-ring-iron-800 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300"><Drop drop={{
        type: DropSize.FULL,
        ...wave.description_drop,
        stableKey: wave.description_drop.id,
        stableHash: wave.description_drop.id
      }} showWaveInfo={false} activeDrop={null} dropViewDropId={null} onReplyClick={_temp2} showReplyAndQuote={false} location={DropLocation.WAVE} onReply={_temp3} previousDrop={null} nextDrop={null} onQuoteClick={_temp4} /></div>, document.body);
    $[14] = isOpen;
    $[15] = placement;
    $[16] = wave.description_drop;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
};
export default WaveDescriptionPopover;
function _temp(prev) {
  return !prev;
}
function _temp2() {}
function _temp3() {}
function _temp4() {}